import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { CertificationInfoRequest } from './models/certification-info-request';
import { CertificationInfoResponse } from './models/certification-info-response';
import { ApplicationPageBaseComponent } from '../../../../shared/components/fcc-601/application-page-base/application-page-base.component';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { ActivatedRoute, Router } from '@angular/router';
import { DirectEntryService } from '../shared/services/direct-entry/direct-entry.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { ApplicationFormFieldComponent } from '../../../../shared/components/fcc-601/application-form-field/application-form-field.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DatePipe } from '@angular/common';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { HoverTooltipComponent } from '../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';
import { CertificationInfoMessages } from './constants/certification-info-messages';
import { CertificationInfoStatements } from './constants/certification-info-statements';
import { ApplicationPageFacadeService } from '../shared/services/application-page-facade/application-page-facade.service';

@Component({
    selector: 'app-certification-info',
    templateUrl: './certification-info.component.html',
    imports: [
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatIconModule,
        ApplicationFormFieldComponent,
        MatCheckboxModule,
        RegexInputDirective,
        HoverTooltipComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DatePipe],
})
export class CertificationInfoComponent
    extends ApplicationPageBaseComponent<
        CertificationInfoRequest,
        CertificationInfoResponse,
        CertificationInfoResponse
    >
    implements OnInit, OnDestroy
{
    regExPattern = RegExPattern;
    messages = CertificationInfoMessages;
    statements: string[] = CertificationInfoStatements;

    override form: FormGroup<FormGroupType<CertificationInfoResponse>> =
        new FormGroup<FormGroupType<CertificationInfoResponse>>({
            certifierFirstName: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            certifierMiddleInitial: new FormControl<string | null>(null),
            certifierLastName: new FormControl<string | null>(
                null,
                Validators.required,
            ),
            certifierTitle: new FormControl<string | null>(null),
            certifierSuffix: new FormControl<string | null>(null),
            certifierSignatureDate: new FormControl<string | null>({
                value: null,
                disabled: true,
            }),
            certifierSignature: new FormControl<string | null>({
                value: null,
                disabled: true,
            }),
        });

    private readonly signatureControlKeys: (keyof CertificationInfoResponse)[] =
        [
            'certifierFirstName',
            'certifierMiddleInitial',
            'certifierLastName',
            'certifierSuffix',
        ] as const;

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly router: Router,
        protected override readonly applicationPageFacadeService: ApplicationPageFacadeService,
        private readonly directEntryService: DirectEntryService,
        private readonly datePipe: DatePipe,
    ) {
        super(route, router, applicationPageFacadeService);
    }

    protected override bindForm(response: CertificationInfoResponse): void {
        this.form.patchValue({
            ...response,
            certifierSignatureDate: this.tryFormatDate(
                response.certifierSignatureDate,
            ),
        });
    }

    /**
     * Formats a date only string representation into the MM-dd-yyyy format.
     * @param date Date only represented as a string.
     * @returns MM-dd-yyyy format if there is a value, otherwise null.
     */
    private tryFormatDate(date: string | null | undefined): string | null {
        return date ? this.datePipe.transform(date, 'MM-dd-yyyy') : null;
    }

    protected override getFormDataFromApi(
        applicationId: string,
    ): Observable<CertificationInfoResponse> {
        return this.directEntryService.getCertificationInfo(applicationId);
    }

    protected override saveFormDataToApi(
        request: CertificationInfoRequest,
    ): Observable<StepValidationResult> {
        return this.directEntryService.saveCertificationInfo(request);
    }

    protected override generateRequestObject(): CertificationInfoRequest {
        const formValue = this.form.value as Required<CertificationInfoRequest>;

        return {
            ...formValue,
            applicationId: this.applicationId!,
        };
    }
}
